import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../common/functions/cn';
import { useTopLayerPortal } from '../../../common/hooks/use-top-layer-portal';
import { buttonVariants } from '../../button/button';
import { preventDialogCloseOnEscape } from '../../common-functions';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../input-group/input-group';

const ComboboxRoot = <Value, Multiple extends boolean | undefined = false>({
  autoHighlight = true,
  onOpenChange,
  ...props
}: ComboboxPrimitive.Root.Props<Value, Multiple>) => {
  const handleOpenChange: NonNullable<typeof onOpenChange> = (open, details) => {
    preventDialogCloseOnEscape(open, details);
    onOpenChange?.(open, details);
  };
  return <ComboboxPrimitive.Root autoHighlight={autoHighlight} onOpenChange={handleOpenChange} {...props} />;
};

const ComboboxValue = ({ ...props }: ComboboxPrimitive.Value.Props) => {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
};

const ComboboxTrigger = ({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) => {
  const { t } = useTranslation();
  return (
    <ComboboxPrimitive.Trigger
      aria-label={t(($) => $.comboboxOpen)}
      data-slot="combobox-trigger"
      className={cn(
        buttonVariants({ variant: 'ghost', size: 'icon-xs' }),
        'text-muted-foreground data-pressed:bg-transparent data-pressed:text-muted-foreground data-pressed:hover:bg-muted',
        'in-data-[size=sm]:size-5',
        'group-has-data-[slot=combobox-clear]/input-group:hidden',
        'group-has-data-[slot=combobox-clear]/combobox-chips:hidden',
        className
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon className="pointer-events-none size-4" />
    </ComboboxPrimitive.Trigger>
  );
};

const ComboboxClear = ({ className, ...props }: ComboboxPrimitive.Clear.Props) => {
  const { t } = useTranslation();
  return (
    <ComboboxPrimitive.Clear
      aria-label={t(($) => $.comboboxClear)}
      data-slot="combobox-clear"
      className={cn(
        buttonVariants({ variant: 'ghost', size: 'icon-xs' }),
        'text-muted-foreground',
        'in-data-[size=sm]:size-5',
        className
      )}
      {...props}
    >
      <XIcon className="pointer-events-none size-4" />
    </ComboboxPrimitive.Clear>
  );
};

const ComboboxInput = ({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  size = 'default',
  readOnly,
  id,
  ...props
}: Omit<ComboboxPrimitive.Input.Props, 'size' | 'required' | 'readOnly'> & {
  showTrigger?: boolean;
  showClear?: boolean;
  size?: 'sm' | 'default';
  readOnly?: boolean;
}) => {
  return (
    <ComboboxPrimitive.InputGroup render={<InputGroup size={size} className={cn('w-auto', className)} />}>
      <ComboboxPrimitive.Input
        id={id !== undefined ? String(id) : undefined}
        render={<InputGroupInput disabled={disabled} className="truncate disabled:bg-transparent" />}
        {...props}
      />
      <InputGroupAddon align="inline-end">
        {showClear && !disabled && !readOnly && <ComboboxClear />}
        {showTrigger && <ComboboxTrigger disabled={disabled} />}
      </InputGroupAddon>
      {children}
    </ComboboxPrimitive.InputGroup>
  );
};

const ComboboxContent = ({
  className,
  side = 'bottom',
  sideOffset = 3,
  align = 'start',
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'>) => {
  const { portalAnchor, portalProps, positionerProps } = useTopLayerPortal();

  return (
    <>
      {portalAnchor}
      <ComboboxPrimitive.Portal {...portalProps}>
        <ComboboxPrimitive.Positioner
          {...positionerProps}
          side={side}
          sideOffset={sideOffset}
          align={align}
          alignOffset={alignOffset}
          anchor={anchor}
          collisionAvoidance={{ side: 'flip', align: 'shift', fallbackAxisSide: 'none' }}
          className="isolate z-50"
        >
          <ComboboxPrimitive.Popup
            data-slot="combobox-content"
            data-chips={!!anchor}
            className={cn(
              'group/combobox-content relative flex flex-col overflow-hidden',
              'rounded-md bg-popover text-popover-foreground shadow-md ring-1 ring-border',
              // Match the trigger width but never below a readable floor, capped to the viewport.
              'max-h-(--available-height) w-[max(var(--anchor-width),14rem)] max-w-(--available-width) origin-(--transform-origin)',
              '*:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0',
              '*:data-[slot=input-group]:shadow-none',
              'duration-100',
              'data-open:fade-in-0 data-open:zoom-in-95 data-open:animate-in',
              'data-closed:fade-out-0 data-closed:zoom-out-95 data-closed:animate-out',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
              'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
              'data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2',
              className
            )}
            {...props}
          />
        </ComboboxPrimitive.Positioner>
      </ComboboxPrimitive.Portal>
    </>
  );
};

const ComboboxList = ({ ref, className, ...props }: ComboboxPrimitive.List.Props) => {
  return (
    <ComboboxPrimitive.List
      ref={ref}
      data-slot="combobox-list"
      className={cn(
        // The popup portals in place, so `--combobox-list-max-height` inherits from any caller ancestor.
        'max-h-[min(var(--combobox-list-max-height,18.5rem),calc(var(--available-height)-9px))] min-h-0',
        'scroll-py-1 overflow-y-auto overscroll-contain p-1 data-empty:p-0',
        className
      )}
      {...props}
    />
  );
};

const ComboboxItem = ({ ref, className, children, title, ...props }: ComboboxPrimitive.Item.Props) => {
  // The popup is pinned to the anchor width, so labels must shrink rather than widen the list.
  // String children get a truncating wrapper and a title tooltip for the full text.
  // Element children are expected to truncate themselves, with the item clipping as a backstop.
  const isTextLabel = typeof children === 'string';

  return (
    <ComboboxPrimitive.Item
      ref={ref}
      data-slot="combobox-item"
      title={title ?? (isTextLabel ? children : undefined)}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center gap-2 overflow-hidden rounded-sm',
        'min-h-8 py-1 pr-8 pl-1.5 text-sm outline-hidden',
        'data-highlighted:bg-accent data-highlighted:text-accent-foreground',
        'not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      )}
      {...props}
    >
      {isTextLabel ? <span className="truncate">{children}</span> : children}
      <ComboboxPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
            <CheckIcon className="pointer-events-none" />
          </span>
        }
      />
    </ComboboxPrimitive.Item>
  );
};

const ComboboxGroup = ({ className, ...props }: ComboboxPrimitive.Group.Props) => {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={cn(className)} {...props} />;
};

const ComboboxLabel = ({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) => {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-label"
      className={cn('px-2 py-1.5 text-muted-foreground text-xs', className)}
      {...props}
    />
  );
};

const ComboboxCollection = ({ ...props }: ComboboxPrimitive.Collection.Props) => {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
};

const ComboboxEmpty = ({ className, ...props }: ComboboxPrimitive.Empty.Props) => {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        'hidden w-full justify-center py-2 text-center text-muted-foreground text-sm group-data-empty/combobox-content:flex',
        className
      )}
      {...props}
    />
  );
};

const ComboboxSeparator = ({ className, ...props }: ComboboxPrimitive.Separator.Props) => {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
};

const comboboxActionsVariants = cva('m-0 flex min-w-0 shrink-0 flex-col gap-1 border-border p-2', {
  variants: {
    align: {
      'block-start': 'order-first border-x-0 border-t-0 border-b',
      'block-end': 'order-last border-x-0 border-t border-b-0',
    },
  },
  defaultVariants: {
    align: 'block-end',
  },
});

const ComboboxActions = ({
  className,
  align = 'block-end',
  onMouseDown,
  ...props
}: React.ComponentProps<'fieldset'> & VariantProps<typeof comboboxActionsVariants>) => {
  return (
    <fieldset
      data-slot="combobox-actions"
      data-align={align}
      onMouseDown={(event) => {
        event.preventDefault();
        onMouseDown?.(event);
      }}
      className={cn(comboboxActionsVariants({ align }), className)}
      {...props}
    />
  );
};

const ComboboxChips = ({
  className,
  size = 'default',
  showTrigger = true,
  showClear = false,
  collapseMultiple = false,
  disabled = false,
  readOnly = false,
  children,
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> &
  ComboboxPrimitive.Chips.Props & {
    size?: 'sm' | 'default';
    showTrigger?: boolean;
    showClear?: boolean;
    collapseMultiple?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
  }) => {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      data-size={size}
      data-disabled={disabled}
      className={cn(
        'group/combobox-chips relative flex min-w-0 items-center gap-1 overflow-hidden',
        collapseMultiple ? 'flex-nowrap' : 'flex-wrap',
        'min-h-8 px-2.5 py-1',
        'text-sm',
        'rounded-md border border-input bg-background bg-clip-padding transition-colors',
        'focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50',
        'has-aria-invalid:border-error has-aria-invalid:ring-2 has-aria-invalid:ring-error/20',
        'has-aria-invalid:focus-within:ring-error/60',
        'has-disabled:select-none has-disabled:bg-transparent has-disabled:opacity-50',
        'data-[size=sm]:min-h-7 data-[size=sm]:px-2 data-[size=sm]:py-0.5',
        (showTrigger || showClear) && 'pr-8',
        className
      )}
      {...props}
    >
      {children}
      {(showTrigger || showClear) && (
        <div className="absolute inset-y-0 right-0 flex items-center p-1">
          {showClear && !disabled && !readOnly && <ComboboxClear />}
          {showTrigger && <ComboboxTrigger />}
        </div>
      )}
    </ComboboxPrimitive.Chips>
  );
};

const comboboxChipStyles = cn(
  'flex h-5.25 min-w-0 items-center justify-center gap-1 whitespace-nowrap rounded-sm',
  'bg-muted px-1.5 text-foreground text-xs'
);

/** A selected value shown as a chip outside a combobox, for read-only fields that should match the edit-mode chips. */
const ComboboxValueChip = ({ className, children, ...props }: React.ComponentProps<'span'>) => (
  <span data-slot="combobox-chip" className={cn(comboboxChipStyles, className)} {...props}>
    <span className="truncate">{children}</span>
  </span>
);

// The icon is 12px, so the hit area reaches out to 24px without changing the chip.
const chipButtonStyles = cn(
  'relative ml-0.5 text-muted-foreground opacity-50 hover:opacity-100 data-disabled:pointer-events-none',
  'after:absolute after:-inset-1.5'
);

const ComboboxChip = ({
  className,
  children,
  showRemove = true,
  showClear = false,
  onRemove,
  ...props
}: ComboboxPrimitive.Chip.Props & {
  showRemove?: boolean;
  /** Clears the whole selection instead of removing this chip's value. For summary chips that stand for every value. */
  showClear?: boolean;
  onRemove?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        comboboxChipStyles,
        'has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50',
        className
      )}
      {...props}
    >
      <span className="truncate">{children}</span>
      {showClear ? (
        <ComboboxPrimitive.Clear
          aria-label={t(($) => $.comboboxClear)}
          data-slot="combobox-chip-clear"
          tabIndex={0}
          className={chipButtonStyles}
        >
          <XIcon className="size-3" />
        </ComboboxPrimitive.Clear>
      ) : (
        showRemove && (
          <ComboboxPrimitive.ChipRemove
            aria-label={t(($) => $.comboboxChipRemove)}
            className={chipButtonStyles}
            data-slot="combobox-chip-remove"
            onClick={onRemove}
          >
            <XIcon className="size-3" />
          </ComboboxPrimitive.ChipRemove>
        )
      )}
    </ComboboxPrimitive.Chip>
  );
};

const ComboboxChipsInput = ({ className, ...props }: ComboboxPrimitive.Input.Props) => {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chip-input"
      className={cn(
        'min-w-16 flex-1 outline-hidden',
        'placeholder:text-muted-foreground',
        'disabled:bg-transparent disabled:opacity-50',
        'group-has-data-[slot=combobox-chip]/combobox-chips:min-w-0 group-has-data-[slot=combobox-chip]/combobox-chips:placeholder:text-transparent',
        className
      )}
      {...props}
    />
  );
};

interface ComboboxMultipleLabelProps<T> extends Omit<ComboboxPrimitive.Chip.Props, 'children'> {
  /** Items to show in the title tooltip. Will be converted to strings. */
  selectedItems?: readonly T[];
  /** Optional function to convert items to strings. Defaults to String(). */
  itemToString?: (item: T) => string;
  /** Adds an X that clears the whole selection. */
  showClear?: boolean;
}

const ComboboxMultipleLabel = <T,>({
  className,
  selectedItems,
  itemToString = String as (item: T) => string,
  showClear = false,
  ...props
}: ComboboxMultipleLabelProps<T>) => {
  const { t } = useTranslation();
  const count = selectedItems?.length ?? 0;
  const labels = selectedItems?.map(itemToString) ?? [];
  return (
    <ComboboxChip
      title={labels.join(', ')}
      showRemove={false}
      showClear={showClear}
      className={cn('truncate', className)}
      {...props}
    >
      {t(($) => $.multiple)}({count})
    </ComboboxChip>
  );
};

const useComboboxAnchor = () => {
  return React.useRef<HTMLDivElement | null>(null);
};

const useComboboxFilter = ComboboxPrimitive.useFilter;
const useComboboxFilteredItems = ComboboxPrimitive.useFilteredItems;

type ComboboxRootProps<Value, Multiple extends boolean | undefined = false> = ComboboxPrimitive.Root.Props<
  Value,
  Multiple
>;

interface ComboboxPresetBaseProps<T> {
  /** The items to render. */
  items: readonly T[];
  /** Returns a stable React key for each item. */
  getKey: (item: T) => string | number;
  /** Returns the user-visible label for each item. Also passed to base-ui as `itemToStringLabel`. */
  getLabel: (item: T) => string;
  /** Rendered inside the popup when the filter yields no results. Defaults to the `comboboxEmpty` text. */
  emptyMessage?: React.ReactNode;
  /** When true on multiselect comboboxes, shows "Multiple (N)" chip instead of individual chips when more than one value is selected. */
  collapseMultiple?: boolean;
}

export {
  ComboboxActions,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxClear,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxMultipleLabel,
  type ComboboxMultipleLabelProps,
  type ComboboxPresetBaseProps,
  ComboboxRoot,
  type ComboboxRootProps,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  ComboboxValueChip,
  useComboboxAnchor,
  useComboboxFilter,
  useComboboxFilteredItems,
};
